// Pure shareId router for the share-web SPA.
//
// Mirrors the worker-side validator (`apps/share-worker/functions/api/shares/[shareId].ts`):
// shareId must be 8–64 chars of [a-zA-Z0-9-]. The SPA reads the id straight
// from `location.pathname`, so the router has to reject anything that is not
// exactly a single matching segment (optionally trailing `/`).

const SHARE_PATH_RE = /^\/([a-zA-Z0-9-]{8,64})\/?$/;

/**
 * Extracts a shareId from a pathname.
 *
 * Returns the capture group if `pathname` matches `^/<id>/?$` with id length
 * 8–64 from `[a-zA-Z0-9-]`. Returns `null` for root (`/`), empty, multi-segment
 * paths, or ids shorter than 8 characters.
 */
export function parseShareIdFromPath(pathname: string): string | null {
  const match = SHARE_PATH_RE.exec(pathname);
  return match ? match[1]! : null;
}

/** True when the path is the team-library view (`/library` or `/library/`). */
export function isLibraryPath(pathname: string): boolean {
  return pathname === '/library' || pathname === '/library/';
}

const SETLIST_PATH_RE = /^\/setlist\/([a-zA-Z0-9-]{1,64})\/?$/;

/** Returns `{ setlistId }` for `/setlist/<id>` (optional trailing slash), else null. */
export function isSetlistPath(pathname: string): { setlistId: string } | null {
  const m = SETLIST_PATH_RE.exec(pathname);
  return m ? { setlistId: m[1]! } : null;
}

/** True when the path is the admin view (`/admin` or `/admin/`). */
export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname === '/admin/';
}

/** True when the path is the login view (`/login` or `/login/`). */
export function isLoginPath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/login/';
}

/** True when the path is the team-dashboard view (`/dashboard` or `/dashboard/`). */
export function isDashboardPath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname === '/dashboard/';
}
